import { Folder, File as FileIcon, Download, Trash2 } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import type { ColumnDef } from '@vnetwork/ui/data';
import type { S3Object } from '@/api/s3';
import { formatGiB } from '@/pages/services/oss/storage-format';
import { LinkButton, Mono } from '../../s3-parts';

/** A folder (prefix) or a leaf object row in the browser table. */
export type BrowserRow =
  | { kind: 'prefix'; name: string; full: string }
  | { kind: 'object'; name: string; obj: S3Object };

export interface ObjectRowActions {
  onOpenPrefix: (full: string) => void;
  onDownload: (key: string) => void;
  onDelete: (key: string) => void;
}

const iconStyle = { display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' } as const;

export function objectColumns(actions: ObjectRowActions): ColumnDef<BrowserRow, unknown>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const r = row.original;
        return r.kind === 'prefix' ? (
          <span style={iconStyle}>
            <Folder size={15} style={{ color: 'var(--vn-accent-solid)' }} />
            <LinkButton onClick={() => actions.onOpenPrefix(r.full)}>{r.name}</LinkButton>
          </span>
        ) : (
          <span style={iconStyle}>
            <FileIcon size={15} style={{ color: 'var(--vn-fg-muted)' }} />
            <Mono>{r.name}</Mono>
          </span>
        );
      },
    },
    {
      id: 'size',
      header: 'Size',
      size: 120,
      cell: ({ row }) => (row.original.kind === 'object' ? formatGiB(row.original.obj.size) : '—'),
    },
    {
      id: 'actions',
      header: '',
      size: 110,
      cell: ({ row }) => {
        const r = row.original;
        if (r.kind !== 'object') return null;
        return (
          <div
            style={{ display: 'flex', gap: 'var(--vn-space-1)', justifyContent: 'flex-end' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button size="sm" variant="ghost" aria-label="Download" onClick={() => actions.onDownload(r.obj.key)}>
              <Download size={14} />
            </Button>
            <Button size="sm" variant="ghost" aria-label="Delete" onClick={() => actions.onDelete(r.obj.key)}>
              <Trash2 size={14} />
            </Button>
          </div>
        );
      },
    },
  ];
}
